import React, { useCallback, useEffect, useState, Fragment, useRef, useMemo } from 'react'
import createProvider from '@store/provider'
import useInfiniteScrolling from '@hooks/useInfiniteScrolling'
import usePrevious from '@hooks/usePrevious'
import useMessageVerification from '@hooks/useMessageVerification'
import { connect, useSelector } from 'react-redux'
import Linkton from '@components/linkton'
import Icon from '@components/icon'
import TextArea from '@components/text-area/text-area'
import MessageVerificationErrors from '@components/message-verification/MessageVerificationErrors'
import { LINKS } from '@common/consts'
import { throttle } from 'lodash'
import { fetchConversation, fetchConversationMessages, denormalizeResponse, sendConversationMessage, markConversationAsRead } from '@common/http'
import { selectCurrentUser } from '@store/selectors/backendData'
import Spinner from '@components/spinner'
import usePaginate from '@hooks/usePaginate'
import useAutoResizeTextArea from '@hooks/useAutoResizeTextArea'
import Message from '../message'
import CommunityTitle from '@containers/community/community-title'
import * as styles from './Conversation.module.scss'
import * as verificationStyles from '@components/message-verification/MessageVerification.module.scss'

const Conversation = (props) => {
  const { id } = props
  const messagesContainerRef = useRef(null)

  const currentUser = useSelector(selectCurrentUser)

  const [conversation, setConversation] = useState(null)
  const [newMessage, setNewMessage] = useState('')
  const [defaultAvatarUrl, setDefaultAvatarUrl] = useState(null)

  const {
    emailVerificationError,
    externalLinkError,
    blockedError,
    verificationEmailSent,
    sendingVerificationEmail,
    clearErrors,
    handleSendError,
    handleResendVerificationEmail,
  } = useMessageVerification()

  // fetch conversation on mount
  useEffect(() => {
    fetchConversation({ id }).then((res) => {
      setConversation(denormalizeResponse(res.data))
    })
  }, [id])

  const fetcher = useCallback(async ({ page }) => {
    const res = await fetchConversationMessages({ id, page })
    setDefaultAvatarUrl(res.data.meta.default_avatar_url)
    return res
  }, [id])
  const { isLoading, data, page, fetchNextPage, reset } = usePaginate({ fetcher })
  const denormalized = denormalizeResponse(data)
  const sortedMessages = denormalized.sort((a, b) => new Date(a.updatedAt) - new Date(b.updatedAt))
  const fetchNextPageThrottled = useMemo(() => throttle(fetchNextPage, 250), [fetchNextPage])

  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth'
      })
    }
  }, [])

  const handleScroll = useInfiniteScrolling(fetchNextPageThrottled, 100, 'up')

  const { setTextarea: assignTextareaEl, resetTextareaHeight } = useAutoResizeTextArea({ allowResize: false })

  // scroll to bottom on data fetching of first page
  const previousPage = usePrevious(page)
  useEffect(() => {
    if (previousPage !== page && page === 1) {
      setTimeout(scrollToBottom, 100) // timeout to wait for messages to render so we have something to scroll
    }
  }, [scrollToBottom, page, previousPage, data])

  // mark conversation as id
  useEffect(() => {
    if (conversation) {
      markConversationAsRead(conversation.id)
    }
  }, [conversation])

  // fetch convos on mount
  useEffect(fetchNextPageThrottled, [])

  const submitMessage = () => {
    if (!newMessage) return

    clearErrors()
    sendConversationMessage({ conversationId: conversation.id, content: newMessage })
      .then(() => {
        setNewMessage('')
        reset()
        resetTextareaHeight()
        fetchNextPage()
      })
      .catch(handleSendError)
  }

  const conversationParticipants = conversation?.users?.filter((user) => user.id !== currentUser.guid)

  return (
    <div className={styles.container}>
      {!conversation ? (
        <Spinner />
      ) : (
        <Fragment>
          <CommunityTitle className={styles.communityTitle} title={conversationParticipants.map((user) => user.username).join(',')} showOnDesktop />

          {isLoading && <Spinner />}
          <div className={styles.messages} ref={messagesContainerRef} onScroll={handleScroll}>
            {sortedMessages.map((message) => (
              <Message
                key={message.id}
                users={[message.user]}
                lastUpdated={message.updatedAt}
                message={message.content}
                messageUrl={`${LINKS.MESSAGES}/${conversation.id}`}
                defaultAvatarUrl={defaultAvatarUrl}
              />
            ))}
          </div>

          <div className={styles.writeMessageContainer}>
            {currentUser.confirmedAt ? (
              <div className={styles.writeMessage}>
                <div className={styles.avatar}>
                  <img className={styles.avatarImg} src={currentUser.avatarSmallUrl} />
                </div>
                <TextArea ref={(r) => r && assignTextareaEl(r)} className={styles.textarea} maxLength='2000' onChange={(value) => { setNewMessage(value); clearErrors() }} fontSize='small' isTransparent={false} value={newMessage} placeholder='Write a message' />
                <div className={styles.submit}>
                  <Linkton className={styles.submitButton} onClick={submitMessage} isDisabled={externalLinkError}>Send</Linkton>
                </div>
              </div>
            ) : (
              <div className={verificationStyles.verifyEmailPrompt}>
                <Icon icon='mail' size={24} />
                <small>Verify your email address to send a message!</small>
                {verificationEmailSent ? (
                  <span className={verificationStyles.emailSentConfirmation}>Verification email sent!</span>
                ) : (
                  <Linkton
                    className={styles.submitButton}
                    onClick={handleResendVerificationEmail}
                    disabled={sendingVerificationEmail}
                  >
                    {sendingVerificationEmail ? 'Sending...' : 'Resend verification email'}
                  </Linkton>
                )}
              </div>
            )}
            <MessageVerificationErrors
              emailVerificationError={emailVerificationError}
              externalLinkError={externalLinkError}
              blockedError={blockedError}
              verificationEmailSent={verificationEmailSent}
              sendingVerificationEmail={sendingVerificationEmail}
              handleResendVerificationEmail={handleResendVerificationEmail}
            />
          </div>
        </Fragment>
      )}
    </div>
  )
}

export default createProvider(connect(null, null)(Conversation, false))
